import { ROLES } from "./roles"

/**
 * Sidebar navigation configuration per role.
 * Each entry: { label, icon (boxicons class), path, children?: [...] }
 * Children share the same shape (no further nesting needed).
 */
export const SIDEBAR_MENU = Object.freeze({
  [ROLES.SUPER_ADMIN]: [
    {
      label: "Dashboard",
      icon: "bx bx-home-circle",
      path: "/dashboard",
    },
    {
      label: "National Targets",
      icon: "bx bx-target-lock",
      path: "/national-targets",
    },
    {
      label: "Account Management",
      icon: "bx bx-user-plus",
      path: "/manage-users",
    },
    {
      label: "Registration Counts",
      icon: "bx bx-bar-chart-alt-2",
      path: "/registration-counts",
    },
  ],

  [ROLES.NATIONAL_ALGON_ADMIN]: [
    {
      label: "Dashboard",
      icon: "bx bx-home-circle",
      path: "/dashboard",
    },
    {
      label: "Ward Registrars",
      icon: "bx bx-group",
      path: "/#",
      children: [
        {
          label: "Approved Ward Registrars",
          path: "/view-approved-agent-registrations-by-state",
        },
        {
          label: "Pending Ward Registrars",
          path: "/view-pending-agent-registrations-by-state",
        },
        {
          label: "Declined Ward Registrars",
          path: "/view-declined-agent-registrations-by-state",
        },
        {
          label: "Rejected Ward Registrars",
          path: "/view-rejected-agent-registrations-by-state",
        },
        {
          label: "Failed Ward Registrars",
          path: "/view-failed-agent-registrations",
        },
      ],
    },
  ],

  [ROLES.NATIONAL_ALGON_COORDINATOR]: [
    {
      label: "Dashboard",
      icon: "bx bx-home-circle",
      path: "/dashboard",
    },
    {
      label: "Ward Registrars",
      icon: "bx bx-group",
      path: "/#",
      children: [
        {
          label: "Approved Ward Registrars",
          path: "/view-approved-agent-registrations-by-state",
        },
        {
          label: "Pending Ward Registrars",
          path: "/view-pending-agent-registrations-by-state",
        },
        {
          label: "Declined Ward Registrars",
          path: "/view-declined-agent-registrations-by-state",
        },
        {
          label: "Rejected Ward Registrars",
          path: "/view-rejected-agent-registrations-by-state",
        },
        {
          label: "Failed Ward Registrars",
          path: "/view-failed-agent-registrations",
        },
      ],
    },
  ],

  [ROLES.STATE_ADMIN]: [
    {
      label: "Dashboard",
      icon: "bx bx-home-circle",
      path: "/dashboard",
    },
    {
      label: "Account Management",
      icon: "bx bx-user-plus",
      path: "/manage-users",
    },
    {
      label: "Ward Registrars",
      icon: "bx bx-group",
      path: "/#",
      children: [
        {
          label: "Approved Ward Registrars",
          path: "/view-approved-agent-registrations-by-state",
        },
        {
          label: "Pending Ward Registrars",
          path: "/view-pending-agent-registrations-by-state",
        },
        {
          label: "Declined Ward Registrars",
          path: "/view-declined-agent-registrations-by-state",
        },
        {
          label: "Rejected Ward Registrars",
          path: "/view-rejected-agent-registrations-by-state",
        },
        {
          label: "Failed Ward Registrars",
          path: "/view-failed-agent-registrations",
        },
      ],
    },
    {
      label: "Training Records",
      icon: "bx bx-book-open",
      path: "/training-records-by-state",
    },
    {
      label: "Generate Codes",
      icon: "bx bx-code-block",
      path: "/generate-code",
    },
    {
      label: "Threshold Management",
      icon: "bx bx-slider",
      path: "/threshold-management",
    },
  ],

  [ROLES.STATE_ALGON_CHAIRMAN]: [
    {
      label: "Dashboard",
      icon: "bx bx-home-circle",
      path: "/dashboard",
    },
    {
      label: "Ward Registrars",
      icon: "bx bx-group",
      path: "/#",
      children: [
        {
          label: "Approved Ward Registrars",
          path: "/view-approved-agent-registrations-by-state",
        },
        {
          label: "Pending Ward Registrars",
          path: "/view-pending-agent-registrations-by-state",
        },
        {
          label: "Declined Ward Registrars",
          path: "/view-declined-agent-registrations-by-state",
        },
        {
          label: "Rejected Ward Registrars",
          path: "/view-rejected-agent-registrations-by-state",
        },
        {
          label: "Failed Ward Registrars",
          path: "/view-failed-agent-registrations",
        },
      ],
    },
  ],

  [ROLES.LGA_FOCAL_PERSON]: [
    {
      label: "Dashboard",
      icon: "bx bx-home-circle",
      path: "/dashboard",
    },
    {
      label: "Ward Registrars",
      icon: "bx bx-group",
      path: "/#",
      children: [
        {
          label: "Approved Ward Registrars",
          path: "/view-approved-agent-registrations-by-lga",
        },
        {
          label: "Pending Ward Registrars",
          path: "/pending-agents-registration",
        },
      ],
    },
    {
      label: "Training Records",
      icon: "bx bx-book-open",
      path: "/training-attendance",
    },
    {
      label: "Generate Codes",
      icon: "bx bx-code-block",
      path: "/generate-code",
    },
    {
      label: "Threshold Management",
      icon: "bx bx-slider",
      path: "/threshold-management-lga",
    },
  ],

  [ROLES.LGA_CHAIRMAN]: [
    {
      label: "Dashboard",
      icon: "bx bx-home-circle",
      path: "/dashboard",
    },
    {
      label: "Ward Registrars",
      icon: "bx bx-group",
      path: "/#",
      children: [
        {
          label: "Approved Ward Registrars",
          path: "/view-approved-agent-registrations-by-lga",
        },
        {
          label: "Pending Ward Registrars",
          path: "/pending-agents-registration",
        },
      ],
    },
  ],
})

/**
 * Derived from SIDEBAR_MENU: a map of menu path -> array of roles whose sidebar
 * includes that path (top-level items and children). Route guards use this to
 * mirror menu visibility for access enforcement, keeping the two in sync.
 */
export const MENU_ROUTE_ROLES = (() => {
  const map = {}
  const add = (path, role) => {
    if (!path || path === "/#") return
    if (!map[path]) map[path] = new Set()
    map[path].add(role)
  }
  Object.entries(SIDEBAR_MENU).forEach(([role, items]) => {
    items.forEach(item => {
      add(item.path, role)
      if (item.children) item.children.forEach(child => add(child.path, role))
    })
  })
  return Object.freeze(
    Object.fromEntries(
      Object.entries(map).map(([path, roles]) => [
        path,
        Object.freeze([...roles]),
      ])
    )
  )
})()
